import type { Client } from '@zg-rentals/http-client';
import { ThrottledBatchWriter } from '@zg-rentals/stdlib/async';
import { getEnvironmentOverride, injectAppNameHeader, isDev, isTest } from '@zg-rentals/environment-utils';
import { getGlobalLogger } from '@zg-rentals/logger-base';
import { memoize } from 'es-toolkit';
import { sequence, tryTask, type ChainedError } from '@zg-rentals/stdlib/task';
import { maybe, task, type Maybe, type Task } from 'true-myth';

import type { MarlinJsCountBatchRequestDto } from '@zg-rentals/ts-marlin-client/types/marlin/MarlinJsCountBatchRequestDto';
import type { MarlinJsGaugeBatchRequestDto } from '@zg-rentals/ts-marlin-client/types/marlin/MarlinJsGaugeBatchRequestDto';
import type { MarlinJsMeasurementBatchRequestDto } from '@zg-rentals/ts-marlin-client/types/marlin/MarlinJsMeasurementBatchRequestDto';
import {
  MarlinJsMetricsCollectionHandler_addCounts,
  MarlinJsMetricsCollectionHandler_addGauges,
  MarlinJsMetricsCollectionHandler_addMeasurements,
} from '@zg-rentals/ts-marlin-client/webapp-client/marlin/MarlinJsMetricsCollectionHandler';
import {
  MarlinJsRefererMetricsCollectionHandler_addCounts,
  MarlinJsRefererMetricsCollectionHandler_addGauges,
  MarlinJsRefererMetricsCollectionHandler_addMeasurements,
} from '@zg-rentals/ts-marlin-client/webapp-client/marlin/MarlinJsRefererMetricsCollectionHandler';

export const Handlers = {
  referer: {
    addCounts: MarlinJsRefererMetricsCollectionHandler_addCounts,
    addGauges: MarlinJsRefererMetricsCollectionHandler_addGauges,
    addMeasurements: MarlinJsRefererMetricsCollectionHandler_addMeasurements,
  },
  direct: {
    addCounts: MarlinJsMetricsCollectionHandler_addCounts,
    addGauges: MarlinJsMetricsCollectionHandler_addGauges,
    addMeasurements: MarlinJsMetricsCollectionHandler_addMeasurements,
  },
};

function enrichPayload<P>(payload: P & { browserTime?: number; environment?: string }): P {
  if (typeof window !== 'undefined') {
    payload.browserTime = Date.now();
  }

  getEnvironmentOverride().inspect((environment) => {
    payload.environment = environment;
  });

  return payload;
}

export const unlessDevOrTest = <T extends {}, E>(getClient: () => Task<Maybe<T>, E>): Task<Maybe<T>, E> =>
  isDev() || isTest() ? task.resolve(maybe.nothing()) : getClient();

export class Marlin {
  private readonly counts: ThrottledBatchWriter<MarlinJsCountBatchRequestDto, void, string>;
  private readonly gauges: ThrottledBatchWriter<MarlinJsGaugeBatchRequestDto, void, string>;
  private readonly measures: ThrottledBatchWriter<MarlinJsMeasurementBatchRequestDto, void, string>;

  protected client: () => Task<Maybe<Client>, string>;

  constructor(
    appName: string,
    getClient: () => Task<Maybe<Client>, string>,
    handlers: (typeof Handlers)['direct'] | (typeof Handlers)['referer'],
    intervalMs: number,
    private readonly bucketMs = 5_000,
  ) {
    // `getClient` allows derivatives to produce a custom client, optionally async, for injecting
    // secrets, etc. Nothing means there is no way to send, so batches are dropped. `memoize` ensures
    // the underlying construction happens just once and the result can be `await`ed as many times as necessary
    this.client = memoize(() =>
      getClient()
        .map((maybeClient) =>
          maybeClient.map((client) =>
            client.extend(({ prefixUrl = 'https://marlin.hotpads.com' }) => ({
              prefixUrl,
              headers: injectAppNameHeader({}, appName),
            })),
          ),
        )
        .inspectRejected((error) => getGlobalLogger('marlin')?.trace(error, 'client error')),
    );

    const onFlushError = (variant: string) => (reason: string) =>
      getGlobalLogger('marlin')?.trace(reason, `${variant} flush error`);

    this.counts = new ThrottledBatchWriter(
      (batch: Array<MarlinJsCountBatchRequestDto>) =>
        tryTask('marlin counts merge', () => {
          const merged = batch.reduce<Record<string, Record<string, MarlinJsCountBatchRequestDto>>>((counts, count) => {
            const { serverName = '', serviceName = '' } = count;

            counts[serverName] ??= {};

            counts[serverName][serviceName] ??= {
              ...count,
              serverName,
              serviceName,
              countStats: {},
            };

            const bucket = counts[serverName][serviceName];

            Object.entries(count.countStats).forEach(([ms, countsForMs]) => {
              const numberMs = Number(ms);
              const clampedMs = numberMs - (numberMs % this.bucketMs);

              bucket.countStats[clampedMs] ??= {};

              Object.entries(countsForMs).forEach(([name, { sum }]) => {
                bucket.countStats[clampedMs][name] ??= { sum: 0 };

                bucket.countStats[clampedMs][name].sum += Math.round(sum);
              });
            });

            return counts;
          }, {});

          return merged;
        }).andThen(this.flush(handlers.addCounts)),
      intervalMs,
      { onFlushError: onFlushError('counts') },
    );

    this.gauges = new ThrottledBatchWriter(
      (batch: Array<MarlinJsGaugeBatchRequestDto>) =>
        tryTask('marlin gauges merge', () => {
          const merged = batch.reduce<Record<string, Record<string, MarlinJsGaugeBatchRequestDto>>>((gauges, gauge) => {
            const { serverName = '', serviceName = '' } = gauge;

            gauges[serverName] ??= {};

            gauges[serverName][serviceName] ??= {
              ...gauge,
              serverName,
              serviceName,
              gauges: {},
            };

            const bucket = gauges[serverName][serviceName];

            Object.entries(gauge.gauges).forEach(([ms, gaugesForMs]) => {
              const numberMs = Number(ms);
              const clampedMs = numberMs - (numberMs % this.bucketMs);

              bucket.gauges[clampedMs] ??= {};

              Object.entries(gaugesForMs).forEach(([name, { sum, min, max, count }]) => {
                if (bucket.gauges[clampedMs][name]) {
                  bucket.gauges[clampedMs][name].count += count;
                  bucket.gauges[clampedMs][name].sum += Math.round(sum);
                  bucket.gauges[clampedMs][name].min = Math.min(bucket.gauges[clampedMs][name].min, Math.round(min));
                  bucket.gauges[clampedMs][name].max = Math.max(bucket.gauges[clampedMs][name].max, Math.round(max));
                } else {
                  bucket.gauges[clampedMs][name] = {
                    count,
                    sum: Math.round(sum),
                    min: Math.round(min),
                    max: Math.round(max),
                  };
                }
              });
            });

            return gauges;
          }, {});

          return merged;
        }).andThen(this.flush(handlers.addGauges)),
      intervalMs,
      { onFlushError: onFlushError('gauges') },
    );

    this.measures = new ThrottledBatchWriter(
      (batch: Array<MarlinJsMeasurementBatchRequestDto>) =>
        tryTask('marlin measures merge', () => {
          const merged = batch.reduce<Record<string, Record<string, MarlinJsMeasurementBatchRequestDto>>>(
            (measurements, measurement) => {
              const { serverName = '', serviceName = '' } = measurement;

              measurements[serverName] ??= {};

              measurements[serverName][serviceName] ??= {
                ...measurement,
                serverName,
                serviceName,
                measurements: {},
              };

              const bucket = measurements[serverName][serviceName];

              Object.entries(measurement.measurements).forEach(([name, measurementsForName]) => {
                bucket.measurements[name] ??= [];

                measurementsForName.forEach((measure) => {
                  const existing = bucket.measurements[name].find((point) => point.timeMs === measure.timeMs);

                  if (existing) {
                    existing.value += measure.value;
                  } else {
                    bucket.measurements[name].push({ ...measure });
                  }
                });
              });

              return measurements;
            },
            {},
          );

          return merged;
        }).andThen(this.flush(handlers.addMeasurements)),
      intervalMs,
      { onFlushError: onFlushError('measures') },
    );
  }

  private flush<P extends object>(send: (payload: P, client: Client) => PromiseLike<unknown>) {
    return (merged: Record<string, Record<string, P>>): Task<void, string> =>
      this.client()
        .orElse(() => task.resolve(maybe.nothing<Client>()))
        .andThen((maybeClient) =>
          maybeClient.mapOr(task.resolve<Array<unknown>, string>([]), (client) =>
            sequence(
              Object.entries(merged).flatMap(([serverName, serverPayloads]) =>
                Object.entries(serverPayloads).map(
                  ([serviceName, payload]) =>
                    () =>
                      tryTask('send', () => send(enrichPayload({ ...payload, serverName, serviceName }), client)),
                ),
              ),
            ),
          ),
        )
        .map(() => undefined);
  }

  public sendCounts(counts: MarlinJsCountBatchRequestDto): Task<void, string | ChainedError> {
    const isInvalid = Object.values(counts.countStats).some((countsForMs) =>
      Object.values(countsForMs).some((count) => !Number.isFinite(count.sum)),
    );

    if (isInvalid) {
      getGlobalLogger('marlin')?.warn(`Invalid counts: ${JSON.stringify(counts)}`);
      return task.resolve(undefined);
    }

    return this.counts.write(counts);
  }

  public sendGauges(gauges: MarlinJsGaugeBatchRequestDto): Task<void, string | ChainedError> {
    const isInvalid = Object.values(gauges.gauges).some((gaugesForMs) =>
      Object.values(gaugesForMs).some((gauge) => Object.values(gauge).some((num) => !Number.isFinite(num))),
    );

    if (isInvalid) {
      getGlobalLogger('marlin')?.warn(`Invalid gauges: ${JSON.stringify(gauges)}`);
      return task.resolve(undefined);
    }

    return this.gauges.write(gauges);
  }

  public sendMeasure(measures: MarlinJsMeasurementBatchRequestDto): Task<void, string | ChainedError> {
    const isInvalid = Object.values(measures.measurements).some((measurementsForMs) =>
      Object.values(measurementsForMs).some((measurement) =>
        Object.values(measurement).some((num) => !Number.isFinite(num)),
      ),
    );

    if (isInvalid) {
      getGlobalLogger('marlin')?.warn(`Invalid measures: ${JSON.stringify(measures)}`);
      return task.resolve(undefined);
    }

    return this.measures.write(measures);
  }

  public sendMeasurementWithGauges(measures: MarlinJsMeasurementBatchRequestDto): Task<void, string | ChainedError> {
    const gaugeTask = this.sendGauges({
      ...measures,
      gauges: Object.entries(measures.measurements).reduce<MarlinJsGaugeBatchRequestDto['gauges']>(
        (acc, [metricName, currMeasures]) => {
          currMeasures.forEach(({ timeMs, value: amount }) => {
            acc[timeMs] ||= {};
            if (acc[timeMs][metricName]) {
              acc[timeMs][metricName].sum += amount;
              acc[timeMs][metricName].count += 1;
              acc[timeMs][metricName].min = Math.min(acc[timeMs][metricName].min, amount);
              acc[timeMs][metricName].max = Math.max(acc[timeMs][metricName].max, amount);
            } else {
              acc[timeMs][metricName] = {
                sum: amount,
                count: 1,
                min: amount,
                max: amount,
              };
            }
          });
          return acc;
        },
        {},
      ),
    });

    return task.all([gaugeTask, this.sendMeasure(measures)]).map(() => undefined);
  }
}
